/**
 * Aviso de banimento — a tela que trava tudo.
 *
 * Enquanto ele estiver na frente, não há como fechar, apertar Esc, clicar fora
 * ou usar o app por baixo. É deliberado: a pessoa precisa ler por que foi
 * banida antes de sair, e sair é a única coisa que o botão faz.
 *
 * O bloqueio aqui é de EXPERIÊNCIA, não de segurança. O servidor já invalidou
 * as sessões dela no instante do banimento — mesmo que alguém apagasse este
 * componente pelo console, nada que tentasse fazer seria aceito.
 */
import { useEffect, type ReactElement } from "react";
import { useApp } from "@/state/store";
import { C, FONT_MONO, hexA } from "@/theme";

export function AvisoBanido(): ReactElement | null {
  const { state, actions } = useApp();
  const aviso = state.banimento;

  // Trava a rolagem da página por baixo e come o Esc: o modal não é
  // dispensável, e um Esc que "quase" fecha dá a impressão errada.
  useEffect(() => {
    if (!aviso) return;
    const antes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const comerTeclas = (e: KeyboardEvent) => {
      if (e.key === "Escape") e.preventDefault();
    };
    window.addEventListener("keydown", comerTeclas, true);
    return () => {
      document.body.style.overflow = antes;
      window.removeEventListener("keydown", comerTeclas, true);
    };
  }, [aviso]);

  if (!aviso) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999, // acima de tudo, inclusive do painel dos fundadores
        display: "grid",
        placeItems: "center",
        background: hexA("#000000", 0.88),
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        style={{
          width: "min(520px, 92vw)",
          padding: "28px 26px",
          border: `1px solid ${hexA(C.red, 0.5)}`,
          borderRadius: 16,
          background: C.stage,
          boxShadow: `0 24px 70px ${hexA("#000000", 0.6)}`,
        }}
      >
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: ".14em",
            color: C.red,
            textTransform: "uppercase",
          }}
        >
          Conta banida
        </div>

        <h2 style={{ fontSize: 21, fontWeight: 700, color: C.textStrong, margin: "10px 0 14px" }}>
          Você foi banido do LFRCORD
        </h2>

        <p style={{ fontSize: 14, lineHeight: 1.65, color: C.textSoft, margin: "0 0 16px" }}>
          O acesso a esta conta foi encerrado por decisão da equipe do LFRCORD.
        </p>

        <div
          style={{
            padding: "12px 14px",
            border: `1px solid ${C.lineSoft}`,
            borderRadius: 10,
            background: C.field,
            marginBottom: 18,
          }}
        >
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 10,
              letterSpacing: ".12em",
              color: C.textFaint,
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Motivo
          </div>
          <div style={{ fontSize: 14, color: C.textStrong, lineHeight: 1.55 }}>{aviso.motivo}</div>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 10,
              letterSpacing: ".1em",
              color: C.textDim,
              marginTop: 10,
            }}
          >
            POR {aviso.por.toUpperCase()} · {new Date(aviso.em).toLocaleString("pt-BR")}
          </div>
        </div>

        <p style={{ fontSize: 12.5, lineHeight: 1.6, color: C.textDim, margin: "0 0 18px" }}>
          Ao confirmar, sua sessão será encerrada. Se você acredita que houve engano, procure a
          equipe do LFRCORD por fora do aplicativo.
        </p>

        <button
          type="button"
          autoFocus
          onClick={actions.aceitarBanimento}
          style={{
            width: "100%",
            padding: "12px 0",
            border: 0,
            borderRadius: 10,
            background: C.red,
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Estou ciente
        </button>
      </div>
    </div>
  );
}
